import { container, containerDenseless, containerDenselessNoMargin, containerDenselessNoMargin_Win32Style, neutralPaletteBackground, fullBleed, containerDenseless_Win32Style, } from "./ReadingPaneContainer.scss";
import ReadingPane from "./ReadingPane";
import { READING_PANE_CONTAINER_ID, READING_PANE_MIN_VISIBLE_HEIGHT } from "../constants";
import { observer } from "owa-mobx-react";
import { composeStore } from "owa-mail-compose-store";
import { FullCompose } from "owa-mail-compose-view";
import { messageAdListViewStatusStore } from "owa-mail-messagead-list-store";
import { MessageAdReadingPane } from "owa-mail-messagead-readingpane-view";
import { MultiSelectReadingPane } from "owa-mail-multiselect-reading-pane-view";
import { useBooleanState } from "owa-react-hooks/lib/useBooleanState";
import { useCustomTimeout } from "owa-react-hooks/lib/useCustomTimeout";
import { subscribeToResizeEvent } from "owa-resize-event";
import type { TabViewState, MailComposeTabViewState } from "owa-tab-store";
import { findTabByData, getActiveContentTab, TabType } from "owa-tab-store";
import { trace } from "owa-trace";
import React from "react";
import isAnySxSVisibleInMainWindow from "owa-sxs-store/lib/utils/isAnySxSVisibleInMainWindow";
import classNames from "owa-classnames";
import { isComposeMailboxStateValid } from "owa-mail-compose-actions/lib/utils/isComposeMailboxStateValid";
import { getAccountAuthStateForMailboxInfo, StateTypeEnum } from "owa-account-authstate-store";
import { isAccountFeatureEnabled, isGlobalFeatureEnabled } from "owa-feature-flags";
import { isCapabilityEnabled } from "owa-capabilities";
import { bleedThroughCapability } from "owa-capabilities-definitions/lib/bleedThroughCapability";
import { getCurrentThemeId } from "owa-theme";
import { getIsMultipleSelection } from "owa-mail-list-store/lib/utils/getIsMultipleSelection";
import getIsSearchTableShown from "owa-mail-list-store/lib/utils/getIsSearchTableShown";
import { getIsLegacySearchHeaderShown } from "owa-mail-list-store";
import { isReadingPanePositionBottom, isReadingPanePositionOff, isReadingPanePositionRight } from "owa-mail-layout";
import { handleComposeTab } from "owa-addins-onsend-itemswitch";
import getAllComposeTabs from "../utils/getAllComposeTabs";
import { findAddinTriggeredComposeViewState } from "../utils/findAddinTriggeredComposeViewState";
import { wrapForcedLayout } from "owa-performance";
import { findAddinTriggeredComposeId } from "../utils/findAddinTriggeredComposeId";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { isMyOutlookFullPageShowing, shouldShowMyOutlookReadingPane, } from "owa-personal-os-store/lib/selectors/getReadingPaneState";
import { LazyPersonalOSReadingPane } from "./LazyPersonalOSReadingPane";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";

const personalOSContainerStyle: React.CSSProperties = { width: "100%", height: "100%" };
const basePersonalOSChromeThemeIds: readonly string[] = [
    "base",
    "base.dark",
    "basewithofficethemepreferred",
    "officeneutral",
    "modern_officeneutral",
];

export interface ReadingPaneContainerProps {
    isDumpsterOrDumpsterSearchTable?: boolean;
    isNotesFolder?: boolean;
    isItemAttachment?: boolean;
}

const defaultExport: React.FunctionComponent<ReadingPaneContainerProps> = observer(function ReadingPaneContainer(props: ReadingPaneContainerProps) {
    const [maximizeScrollRegion, setMaximizeScrollRegionRequired, setMaximizeScrollRegionNotRequired,] = useBooleanState(false);
    
    const isWin32Style = isWin32StylingEnabled();
    
    /**
     * This function is used to determine if the reading pane has
     * enough height for reading an email properly. If it does not,
     * the maximizeScrollRegion flag is set to allow scrolling the
     * email subject and avoid the sticky item header, hence there
     * will be more space to read(the scroll region is maximized)
     */
    const checkMaximizeScrollRegionRequired = () => {
        let readingPaneContainerHeight;
        wrapForcedLayout("ReadingPaneContainer_readingPaneContainerHeight", () => {
            readingPaneContainerHeight =
                /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
                 * This is baseline exception, if you edit this file you need to fix this exception.
                 * Baseline, please provide a proper justification if touching this code
                 *	> 'clientHeight' is restricted from being used. This property can cause performance problems by causing re-layouts. Please use a resize observer instead. */
                document.getElementById("ReadingPaneContainerId")?.clientHeight;
        });
        readingPaneContainerHeight &&
            readingPaneContainerHeight < READING_PANE_MIN_VISIBLE_HEIGHT
            ? setMaximizeScrollRegionRequired()
            : setMaximizeScrollRegionNotRequired();
    };
    
    const [setResizeTimer] = useCustomTimeout("ReadingPaneContainer");
    
    const readingPaneContainerRef = React.useRef<HTMLDivElement>(null);
    
    const onResize = () => setResizeTimer(() => {
        checkMaximizeScrollRegionRequired();
    }, 1);
    
    React.useEffect(() => {
        // Always check when rendering the RP component in case
        // zoom has been set before resizing
        setTimeout(checkMaximizeScrollRegionRequired, 0);
        const resizeEventScope = ""; //default
        return subscribeToResizeEvent(onResize, resizeEventScope, window);
    }, []);
    
    // This effect runs when `addinTriggeredComposeId` or `activeContentTab` changes.
    // It checks if the feature 'addin-enableItemSwitchforSmartAlertsV2' is enabled
    // and if `addinTriggeredComposeId` is defined. If both conditions are met,
    // it finds the corresponding tab and, if the tab is different from the active content tab,
    // it calls `handleComposeTab` with the found tab.
    const addinTriggeredComposeId = findAddinTriggeredComposeId();
    const activeContentTab = getActiveContentTab();
    
    const isBottomReadingPane = isReadingPanePositionBottom();
    const isSearchTableShown = getIsSearchTableShown();
    // The legacy search header sits directly above the panes, so (like the bottom position)
    // the Reading Pane drops its top inset to attach to it.
    const shouldDropTopMargin = isBottomReadingPane || getIsLegacySearchHeaderShown();
    // The Win32 bottom-pane treatment (thin divider + flat frame) is only applied when the
    // Reading Pane shows a message (Primary tab); MailView suppresses it for compose. Mirror
    // that here so the Win32 zero-margin override does not strip the compose right gutter.
    const isPrimaryReadingPaneTab = !activeContentTab || activeContentTab.type === TabType.Primary;
    // The Win32 Reading Pane wrapper in MailView already supplies the right gutter for the
    // bottom, right and off positions, so drop the container's own gutter to avoid doubling it.
    const shouldRemoveWin32RightMargin = isWin32Style &&
        isPrimaryReadingPaneTab &&
        (isBottomReadingPane || isReadingPanePositionRight() || isReadingPanePositionOff());
    const hasNonBaseTheme = !basePersonalOSChromeThemeIds.includes(getCurrentThemeId());
    const isBleedThroughEnabled = isCapabilityEnabled(bleedThroughCapability);
    
    React.useEffect(() => {
        if (!isGlobalFeatureEnabled("addin-enableItemSwitchforSmartAlertsV2") ||
            !addinTriggeredComposeId) {
            return;
        }
        const currentTab = findTabByData(addinTriggeredComposeId) as MailComposeTabViewState;
        if (activeContentTab?.id !== currentTab?.id) {
            handleComposeTab(currentTab ?? ({} as MailComposeTabViewState)); // If current tab is undefined then it is a draft scenario
        }
    }, [addinTriggeredComposeId, activeContentTab]);
    
    const renderReadingPane = (): React.JSX.Element => {
        const useMultiSelectReadingPane = getIsMultipleSelection();
        let renderContent = null;
        const activeTab = getActiveContentTab();
        
        const showPersonalOS = shouldShowMyOutlookReadingPane(isMyOutlookFullPageShowing(), isAccountFeatureEnabled("acct-personalos-augloop", getGlobalSettingsAccountMailboxInfo()), isSearchTableShown, useMultiSelectReadingPane, activeTab?.type === TabType.MailCompose ||
            activeTab?.type === TabType.SecondaryReadingPane);
        if (showPersonalOS) {
            return (<div key="renderReadingPaneDiv" style={personalOSContainerStyle}>
                        <LazyPersonalOSReadingPane hasNonBaseTheme={hasNonBaseTheme} hasFolderPaneBackplate={isBleedThroughEnabled}/>
                    </div>);
        }
        
        if (activeTab &&
            (activeTab.type == TabType.MailCompose ||
                activeTab.type == TabType.SecondaryReadingPane)) {
            // For secondary tab, it is not linked to list view, so always ignore what is selected from list view
            // and just render tab content for secondary tabs
            renderContent = renderTabContent(activeTab);
        }
        
        // If in dumpster or dumpster search, should always show reading pane (no compose)
        else if (props.isDumpsterOrDumpsterSearchTable && !useMultiSelectReadingPane) {
            renderContent = (<ReadingPane isDumpsterOrDumpsterSearchTable={props.isDumpsterOrDumpsterSearchTable} isFromMainWindow={true} isItemAttachment={props.isItemAttachment} maximizeScrollRegion={maximizeScrollRegion}/>);
        }
        else if (useMultiSelectReadingPane) {
            renderContent = (<MultiSelectReadingPane isDumpsterOrDumpsterSearchTable={props.isDumpsterOrDumpsterSearchTable} isNotesFolder={props.isNotesFolder}/>);
        }
        else {
            const primaryComposeId = composeStore().primaryComposeId;
            const primaryComposeViewState = primaryComposeId && composeStore().viewStates.get(primaryComposeId);
            // Priority for content to be loaded into the Reading Pane:
            // 1. If a message ad has been clicked.
            // 2. If we're in tab view and have an activate tab matching these types.
            // 3. If a full compose is open.
            // 4. Render the base reading pane out of the store.
            if (messageAdListViewStatusStore().selectedAdId) {
                renderContent = (<MessageAdReadingPane selectedAdRowId={messageAdListViewStatusStore().selectedAdId}/>);
            }
            else if (primaryComposeViewState && !primaryComposeViewState.isInlineCompose) {
                renderContent =
                    !isAnySxSVisibleInMainWindow() &&
                        isComposeMailboxStateValid(primaryComposeViewState) ? (<FullCompose viewState={primaryComposeViewState} key={primaryComposeViewState.composeId}/>) : null;
            }
            else {
                const shouldRenderReadingPane = !primaryComposeViewState ||
                    isComposeMailboxStateValid(primaryComposeViewState);
                if (isGlobalFeatureEnabled("addin-enableItemSwitchforSmartAlertsV2") &&
                    addinTriggeredComposeId &&
                    getActiveContentTab()?.type === TabType.MailCompose) {
                    const currentTab = findTabByData(addinTriggeredComposeId) as MailComposeTabViewState;
                    renderContent = renderTabContent(currentTab);
                }
                else if (shouldRenderReadingPane) {
                    renderContent = renderReadingPaneValidComposeState();
                }
                else {
                    renderContent = null;
                }
            }
        }
        return <div key="renderReadingPaneDiv">{renderContent}</div>;
    };
    
    // This function is used to render the reading pane when the compose mailbox state is valid
    const renderReadingPaneValidComposeState = () => (<ReadingPane isDumpsterOrDumpsterSearchTable={props.isDumpsterOrDumpsterSearchTable} isFromMainWindow={true} isItemAttachment={props.isItemAttachment} maximizeScrollRegion={maximizeScrollRegion}/>);
    
    const renderTabContent = (tab: TabViewState) => {
        let content: React.JSX.Element | null = null;
        switch (tab.type) {
            case TabType.MailCompose:
                const composeViewState = composeStore().viewStates.get(tab.data);
                if (composeViewState) {
                    const isAnySxSInMainWin: boolean = isAnySxSVisibleInMainWindow();
                    const isMailboxValid: boolean = isComposeMailboxStateValid(composeViewState);
                    composeViewState.perfDatapoint?.addToCustomWaterfall(4, "RenderFullCompose", true /* discardIfDefined */);
                    content =
                        !isAnySxSInMainWin && isMailboxValid ? (<FullCompose key={composeViewState.composeId} viewState={composeViewState}/>) : null;
                }
                else {
                    // for some reason, we are trying to render a tab, but we weren't able to grab any data about that tab.
                    // Tracing here with some info that might help us diagnose.
                    let keyList = "";
                    /* eslint-disable-next-line owa-custom-rules/forbid-foreach-with-variables-outside-of-function-scope -- (https://aka.ms/OWALintWiki)
                     * https://dev.azure.com/outlookweb/Outlook%20Web/_wiki/wikis/Outlook%20Web.wiki/9650/Use-for-const-loop-of-instead-of-forEach
                     *	> When using a forEach function call, avoid using variables outside of the scope of the function, use for (const item of array) instead */
                    composeStore().viewStates.forEach((_value, key) => {
                        keyList = keyList + key + " : ";
                    });
                    trace.warn("[ReadingPaneContainer] Attempted to render compose tab ID " +
                        tab.data +
                        " but it was not present in the compose store. Compose store IDs are " +
                        keyList);
                }
                break;
            case TabType.SecondaryReadingPane:
                const mailboxInfo = tab?.data?.id?.mailboxInfo;
                content =
                    !mailboxInfo ||
                        getAccountAuthStateForMailboxInfo(mailboxInfo).stateType !==
                            StateTypeEnum.AuthError ? (<ReadingPane isDumpsterOrDumpsterSearchTable={props.isDumpsterOrDumpsterSearchTable} isFromMainWindow={true} isItemAttachment={props.isItemAttachment} maximizeScrollRegion={maximizeScrollRegion}/>) : null;
                break;
        }
        return content;
    };
    
    const isRPDenselessEnabled = isGlobalFeatureEnabled("rp-denseless");
    
    React.useEffect(() => {
        // This function will be called when the component unmounts
        return () => {
            const activeComposeTabs = getAllComposeTabs() as MailComposeTabViewState[];
            if (isGlobalFeatureEnabled("addin-enableItemSwitchforSmartAlertsV2") &&
                activeComposeTabs.length > 0) {
                const activeComposeTabWhereOnSendInTriggerred = findAddinTriggeredComposeViewState(activeComposeTabs);
                if (activeComposeTabWhereOnSendInTriggerred) {
                    handleComposeTab(activeComposeTabWhereOnSendInTriggerred);
                }
            }
        };
    }, []);
    return (<div className={classNames(isRPDenselessEnabled
            ? shouldDropTopMargin
                ? classNames(containerDenselessNoMargin, shouldRemoveWin32RightMargin &&
                    containerDenselessNoMargin_Win32Style)
                : isWin32Style
                    ? containerDenseless_Win32Style
                    : containerDenseless
            : container, isBleedThroughEnabled || isRPDenselessEnabled
            ? fullBleed
            : neutralPaletteBackground)} key="readingPaneContainerDiv" id={READING_PANE_CONTAINER_ID} ref={readingPaneContainerRef}>
                {renderReadingPane()}
            </div>);
}, "ReadingPaneContainer");
export default defaultExport;
